import { ExternalLink, Calendar } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
	BrowserRouter as Router,
	Routes,
	Route,
	useParams,
} from 'react-router-dom'

import ImageModal from './components/ImageModal'
import CalendarModal from './components/appointment/CalendarModal'
import { getUserDataForFrontend } from './function/getUserDataForFrontend'
import StoreAppLink from './src/components/StoreAppLink'
import { usePrestation } from './src/hook/useService'
import { PublicUser } from './types/PublicUser'

// Add interface for schedule
interface TimePeriod {
	availableAt: 'Provider' | 'Client' | 'No Matter'
	timePeriod: string
	occupiedAppointmentId: string | null
}

interface ProviderSchedule {
	_id: string
	scheduleDate: string
	providerId: string
	timePeriods: TimePeriod[]
	createdAt: string
	updatedAt: string
}

interface ApiResponse<T> {
	success: boolean
	message: string
	data: T
}

interface ApiResponseList<T> {
	success: boolean
	message: string
	result: {
		pagination: {
			page: number
			limit: number
			totalPages: number
			totalItems: number
			nextPage: number | null
		}
		list: T[]
	}
	error: null | string
}

export default function App() {
	return (
		<Router>
			<Routes>
				<Route
					path="/:uid"
					element={<UserProfilePage />}
				/>
				<Route
					path="/"
					element={<div>Please provide a user ID</div>}
				/>
			</Routes>
		</Router>
	)
}

function UserProfilePage() {
	const { uid } = useParams()
	const [user, setUser] = useState<PublicUser | null>(null)
	const [prestations, setPrestations] = useState<any[]>([])
	const [schedules, setSchedules] = useState<ProviderSchedule[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [isImageModalOpen, setIsImageModalOpen] = useState(false)
	const [selectedImage, setSelectedImage] = useState<string | null>(null)
	const [currentImageIndex, setCurrentImageIndex] = useState(0)
	const [isGalleryOpen, setIsGalleryOpen] = useState(false)
	const [isCalendarModalVisible, setIsCalendarModalVisible] = useState(false)
	const [selectedDateFormate, setSelectedDateFormate] = useState('')
	const { getPrestation } = usePrestation()
	const [currentServiceImages, setCurrentServiceImages] = useState<string[]>(
		[]
	)

	useEffect(() => {
		;(async () => {
			try {
				// @ts-ignore - TODO: Update getUserDataForFrontend to accept uid parameter
				const userData = await getUserDataForFrontend(uid)
				if (!userData) {
					setError("Impossible de charger l'utilisateur.")
					return
				}
				setUser(userData)
			} catch (err) {
				setError('Erreur inattendue. Veuillez réessayer.')
			} finally {
				setLoading(false)
			}
		})()
	}, [uid])

	useEffect(() => {
		;(async () => {
			if (!user) return
			const res = await getPrestation(user._id)
			if (res?.list) setPrestations(res.list)
		})()
	}, [user])

	// Update effect to fetch schedules using the same approach as apiSlice
	useEffect(() => {
		;(async () => {
			if (!user?._id) return

			try {
				const response = await fetch(
					`${process.env.REACT_APP_API_ENDPOINT}/api/v1/provider-schedule/list?providerId=${user._id}`
				)
				const data: ApiResponseList<ProviderSchedule> =
					await response.json()

				if (data.success) {
					const availableSchedules = data.result.list.filter(
						schedule =>
							schedule.timePeriods.some(
								period => period.occupiedAppointmentId === null
							)
					)
					setSchedules(availableSchedules)
				}
			} catch (err) {
				console.error('Error fetching schedules:', err)
			}
		})()
	}, [user])

	const handleOpenInApp = () => {
		if (!user?._id) return
		const deepLink = `${process.env.REACT_APP_BABACOIFFURE_APP_PREFIX}${user._id}`
		window.location.href = deepLink
	}

	const handleImageClick = (service: any, image: string, index: number) => {
		// Get all images for this service
		const serviceImages = [
			service.attachments.headerImage?.secureURL,
			...service.attachments.galleryImages.map(
				(img: any) => img.secureURL
			),
		].filter(Boolean)

		setCurrentServiceImages(serviceImages)
		setSelectedImage(image)
		setCurrentImageIndex(index)
		setIsGalleryOpen(true)
	}

	const handleCloseGallery = () => {
		setIsGalleryOpen(false)
		setSelectedImage(null)
		setCurrentServiceImages([])
	}

	const handlePrevImage = () => {
		setCurrentImageIndex(prev =>
			prev === 0 ? currentServiceImages.length - 1 : prev - 1
		)
		setSelectedImage(
			currentServiceImages[
				currentImageIndex === 0
					? currentServiceImages.length - 1
					: currentImageIndex - 1
			]
		)
	}

	const handleNextImage = () => {
		setCurrentImageIndex(prev =>
			prev === currentServiceImages.length - 1 ? 0 : prev + 1
		)
		setSelectedImage(
			currentServiceImages[
				currentImageIndex === currentServiceImages.length - 1
					? 0
					: currentImageIndex + 1
			]
		)
	}

	// Add function to format date
	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('fr-FR', {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		})
	}

	// Transform schedules into availability data format for CalendarModal
	const availabilityData = schedules.map(schedule => ({
		date: schedule.scheduleDate.split('T')[0], // Convert to YYYY-MM-DD format
		timeSlots: schedule.timePeriods.map(period => ({
			time: period.timePeriod,
			isAvailable: !period.occupiedAppointmentId,
		})),
	}))

	if (loading) {
		return (
			<div className="bg-primary min-h-screen px-[4%] py-6">
				<div className="mx-auto max-w-4xl">
					<div className="animate-pulse space-y-4 p-6">
						<div className="bg-white80 h-6 w-1/3 rounded" />
						<div className="bg-white80 h-4 w-1/2 rounded" />
						<div className="bg-white80 h-32 w-32 rounded-full" />
					</div>
				</div>
			</div>
		)
	}

	if (error || !user) {
		return (
			<div className="bg-primary min-h-screen px-[4%] py-6">
				<div className="mx-auto max-w-4xl">
					<div className="text-danger p-6">
						<p>{error}</p>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="bg-primary min-h-screen px-[4%] py-6">
			<div className="mx-auto max-w-4xl">
				<div className="mt-4">
					<div className="flex">
						<div className="w-4/5">
							<div className="flex items-center gap-6">
								<div
									className="relative h-32 w-32 cursor-pointer overflow-hidden rounded-full transition-opacity hover:opacity-90"
									onClick={() =>
										user.avatar?.secureURL &&
										setIsImageModalOpen(true)
									}>
									{user.avatar?.secureURL ? (
										<img
											src={user.avatar.secureURL}
											alt={user.name}
											className="h-full w-full object-cover"
										/>
									) : (
										<div className="bg-white80 flex h-full w-full items-center justify-center">
											<span className="font-Outfit text-primary text-4xl">
												{user.name
													.charAt(0)
													.toUpperCase()}
											</span>
										</div>
									)}
								</div>
								<div>
									<h1 className="font-Impact text-[64px] font-normal uppercase text-white">
										{user.name}
									</h1>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="mt-8">
					<h2 className="font-Impact text-white90 text-[28px] font-normal uppercase">
						Disponibilités
					</h2>
					<div className="mt-3 flex gap-4">
						<button
							onClick={() => setIsCalendarModalVisible(true)}
							className="bg-white90 font-Outfit text-darkPrimary flex items-center gap-2 rounded-[32px] px-6 py-3 text-base font-medium transition-colors hover:bg-white">
							<Calendar
								size={20}
								className="text-darkPrimary"
							/>
							Voir les disponibilités
						</button>
						<button
							onClick={handleOpenInApp}
							className="bg-yellow font-Outfit text-darkPrimary hover:bg-yellow/90 flex items-center gap-2 rounded-full px-4 py-3.5 text-base font-medium transition-colors duration-200">
							Ouvrir dans l'app <ExternalLink size={16} />
						</button>
					</div>
				</div>

				<div className="mt-8">
					<h2 className="font-Impact text-white90 text-[28px] font-normal uppercase">
						Services disponibles
					</h2>
					<div className="mt-3 space-y-4">
						{prestations.map(p => (
							<div
								key={p._id}
								className="bg-white90 rounded-[32px] p-4">
								<div className="flex gap-4">
									<div
										className="w-4/10 relative cursor-pointer overflow-hidden rounded-[28px] transition-opacity hover:opacity-90"
										onClick={() =>
											p.attachments.headerImage
												?.secureURL &&
											handleImageClick(
												p,
												p.attachments.headerImage
													.secureURL,
												0
											)
										}>
										<img
											src={
												p.attachments.headerImage
													?.secureURL
											}
											alt={p.name}
											className="h-44 w-full object-cover"
										/>
										<div className="absolute bottom-0 left-0 h-2/5 w-full bg-gradient-to-t from-black/90 to-transparent" />
									</div>
									<div className="w-6/10">
										<div>
											<h3 className="font-Outfit text-darkPrimary text-lg font-medium">
												{p.name}
											</h3>
											<p className="font-Outfit text-primary90 text-sm font-normal">
												{p.description ||
													'Aucune description'}
											</p>
											<p className="font-Outfit text-darkPrimary my-1 text-lg font-medium">
												{p.isSizeBasedAddonsActive &&
												p.sizeBasedAddons.length > 0
													? `à partir de ${Math.min(
															...p.sizeBasedAddons.flatMap(
																(group: {
																	sizes: {
																		price: number
																	}[]
																}) =>
																	group.sizes.map(
																		(size: {
																			price: number
																		}) =>
																			size.price
																	)
															)
														)}€`
													: `${p.price}€`}
											</p>
										</div>
									</div>
								</div>

								{p.isAddonsActive && p.addons.length > 0 && (
									<div className="border-t-white80 mt-4 border-t pt-4">
										<p className="font-Outfit text-darkPrimary text-base font-medium">
											Addons :
										</p>
										<div className="mt-2 space-y-2">
											{p.addons.map((addon: any) => (
												<div
													key={addon._id}
													className="flex justify-between">
													<span className="font-Outfit text-primary90 text-sm font-normal">
														{addon.name}
													</span>
													<span className="font-Outfit text-darkPrimary text-base font-medium">
														{addon.price}€
													</span>
												</div>
											))}
										</div>
									</div>
								)}

								{p.isSizeBasedAddonsActive &&
									p.sizeBasedAddons.length > 0 && (
										<div className="border-t-white80 mt-4 border-t pt-4">
											<p className="font-Outfit text-darkPrimary text-base font-medium">
												Addons par taille :
											</p>
											{p.sizeBasedAddons.map(
												(group: any) => (
													<div
														key={group._id}
														className="mt-2">
														<p className="font-Outfit text-primary text-sm font-semibold">
															{group.name}
														</p>
														<div className="mt-2 space-y-2">
															{group.sizes.map(
																(size: any) => (
																	<div
																		key={
																			size._id
																		}
																		className="flex justify-between">
																		<span className="font-Outfit text-primary90 text-sm font-normal">
																			{
																				size.size
																			}
																		</span>
																		<span className="font-Outfit text-darkPrimary text-base font-medium">
																			{
																				size.price
																			}
																			€
																		</span>
																	</div>
																)
															)}
														</div>
													</div>
												)
											)}
										</div>
									)}

								{p.attachments.galleryImages.length > 0 && (
									<div className="border-t-white80 mt-4 border-t pt-4">
										<p className="font-Outfit text-darkPrimary text-base font-medium">
											Galerie :
										</p>
										<div className="mt-2 grid grid-cols-3 gap-2">
											{p.attachments.galleryImages.map(
												(img: any, index: number) => (
													<div
														key={index}
														className="cursor-pointer transition-opacity hover:opacity-90"
														onClick={() =>
															handleImageClick(
																p,
																img.secureURL,
																index
															)
														}>
														<img
															src={img.secureURL}
															alt=""
															className="h-24 w-full rounded-lg object-cover shadow-sm transition-shadow duration-200 hover:shadow-md"
														/>
													</div>
												)
											)}
										</div>
									</div>
								)}
							</div>
						))}
					</div>
				</div>
			</div>

			{selectedImage && (
				<ImageModal
					isOpen={isImageModalOpen}
					onClose={() => {
						setIsImageModalOpen(false)
						setSelectedImage(null)
					}}
					imageUrl={selectedImage}
					alt={user?.name || ''}
				/>
			)}

			<CalendarModal
				isCalendarModalVisible={isCalendarModalVisible}
				setIsCalendarModalVisible={setIsCalendarModalVisible}
				availabilityData={availabilityData}
				isLoading={loading}
				setSelectedDateFormate={setSelectedDateFormate}
				selectedDateFormate={selectedDateFormate}
				appointmentData={[]}
				minDate={new Date()}
			/>

			{/* Image Gallery Modal */}
			{isGalleryOpen && selectedImage && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
					<button
						onClick={handleCloseGallery}
						className="absolute right-4 top-4 text-4xl text-white hover:text-gray-300">
						×
					</button>
					<button
						onClick={handlePrevImage}
						className="absolute left-4 text-4xl text-white hover:text-gray-300">
						‹
					</button>
					<button
						onClick={handleNextImage}
						className="absolute right-4 text-4xl text-white hover:text-gray-300">
						›
					</button>
					<img
						src={selectedImage}
						alt="Gallery"
						className="max-h-[90vh] max-w-[90vw] object-contain"
					/>
				</div>
			)}
		</div>
	)
}
